import React from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import { SecondaryButton } from "Shared/Buttons";
import styled from "styled-components";
import {
    BlockContainer,
    BlockDescription,
    BlockHeader,
} from "src/components/ContinueDealerOffer/PageComponents/Blocks";
import { PageContent } from "src/components/ContinueDealerOffer/PageComponents/PageContent";
import { PageHeader } from "src/components/ContinueDealerOffer/PageComponents/PageHeader";
import Accessories from "src/components/ContinueDealerOffer/Pages/ReviewPage/Details/Accessories";
import Cash from "src/components/ContinueDealerOffer/Pages/ReviewPage/Details/Cash";
import CustomerInformation from "src/components/ContinueDealerOffer/Pages/ReviewPage/Details/CustomerInformation";
import FeesAndTaxes from "src/components/ContinueDealerOffer/Pages/ReviewPage/Details/FeesAndTaxes";
import Lease from "src/components/ContinueDealerOffer/Pages/ReviewPage/Details/Lease";
import Loan from "src/components/ContinueDealerOffer/Pages/ReviewPage/Details/Loan";
import PriceBreakdown from "src/components/ContinueDealerOffer/Pages/ReviewPage/Details/PriceBreakdown";
import TradeIn from "src/components/ContinueDealerOffer/Pages/ReviewPage/Details/TradeIn";
import VehicleInformation from "src/components/ContinueDealerOffer/Pages/ReviewPage/Details/VehicleInformation";
import Warranties from "src/components/ContinueDealerOffer/Pages/ReviewPage/Details/Warranties";
import CustomOffer from "src/components/icons/CustomOffer";
import { ApplicationStore } from "src/store";
import {
    getDealerOfferAccessoriesByVin,
    getDealerOfferAmountFinancedByVin,
    getDealerOfferDistanceByVin,
    getDealerOfferDownPaymentByVin,
    getDealerOfferFeesByVin,
    getDealerOfferInterestRateByVin,
    getDealerOfferLenderByVin,
    getDealerOfferPriceByVin,
    getDealerOfferRebatesByVin,
    getDealerOfferTaxesByVin,
    getDealerOfferTermByVin,
    getDealerOfferTradeInAllowanceByVin,
    getDealerOfferTradeInBalanceByVin,
    getDealerOfferTradeInCashOutByVin,
    getDealerOfferTypeByVin,
    getDealerOfferWarrantiesByVin,
} from "src/store/selectors/dealerOffer";
import { getVehicleMsrp, getVehicleOdometer, getVehicleStock, getVehicleTitle } from "src/store/selectors/vehicles";

interface Props {
    vin: string;
}

const ReviewPage = ({
    vin,
    title,
    stock,
    odometer,
    firstName,
    lastName,
    address,
    address2,
    city,
    state,
    zip,
    msrp,
    price,
    tradeAllowance,
    tradeBalance,
    tradeCashOut,
    fees,
    taxes,
    accessories,
    warranties,
    rebates,
    paymentType,
    downPayment,
    lender,
    term,
    rate,
    amountFinanced,
    distance,
    distanceLabel,
    account_disclaimers,
}: Props & ReturnType<typeof mapStateToProps>) => {
    const { t } = useTranslation();
    const history = useHistory();

    return (
        <PageContent className={`ReviewPage`}>
            <PageHeader>{t("DealerOffer.ContinueDealerOffer.ReviewPage.Header")}</PageHeader>

            <BlockContainer icon={<CustomOffer />}>
                <BlockHeader>{t("DealerOffer.ContinueDealerOffer.ReviewPage.SubHeader")}</BlockHeader>

                <DetailsContainer>
                    <VehicleInformation title={title} vin={vin} stock={stock} odometer={odometer} />

                    <CustomerInformation
                        firstName={firstName}
                        lastName={lastName}
                        address={address}
                        address2={address2}
                        city={city}
                        state={state}
                        zip={zip}
                    />

                    <PriceBreakdown msrp={msrp} price={price} rebates={rebates} />

                    <FeesAndTaxes fees={fees} taxes={taxes} />

                    <Accessories accessories={accessories} />

                    <Warranties warranties={warranties} />

                    <TradeIn tradeAllowance={tradeAllowance} tradeBalance={tradeBalance} tradeCashOut={tradeCashOut} />

                    {(() => {
                        switch (paymentType) {
                            case "loan":
                                return (
                                    <Loan
                                        lender={lender}
                                        downPayment={downPayment}
                                        downPaymentLabel={account_disclaimers?.down_payment_label_loan}
                                        amountFinanced={amountFinanced}
                                        rate={rate}
                                        term={term}
                                    />
                                );
                            case "lease":
                                return (
                                    <Lease
                                        lender={lender}
                                        downPayment={downPayment}
                                        downPaymentLabel={account_disclaimers?.down_payment_label_lease}
                                        amountFinanced={amountFinanced}
                                        distance={distance}
                                        distanceLabel={distanceLabel}
                                        term={term}
                                    />
                                );
                            default:
                                return <Cash amountFinanced={amountFinanced} />;
                        }
                    })()}
                </DetailsContainer>

                <BlockDescription>{t("DealerOffer.ContinueDealerOffer.ReviewPage.Disclaimer")}</BlockDescription>
            </BlockContainer>

            <SecondaryButton
                onClick={() => history.push("/dealer-offer")}
                className={`ContinueDealerOffer__BackButton`}
            >
                {t("DealerOffer.ContinueDealerOffer.ReviewPage.BackButton")}
            </SecondaryButton>
        </PageContent>
    );
};

const mapStateToProps = (state: ApplicationStore, { vin }: Props) => ({
    account_disclaimers: state.account.account_disclaimers,
    title: getVehicleTitle(state, vin),
    stock: getVehicleStock(state, vin),
    odometer: getVehicleOdometer(state, vin),
    firstName: state.deal.person?.first_name,
    lastName: state.deal.person?.last_name,
    address: state.deal.person?.street1,
    address2: state.deal.person?.street2,
    city: state.deal.person?.city,
    state: state.deal.person?.state,
    zip: state.deal.person?.zip,
    msrp: getVehicleMsrp(state, vin),
    price: getDealerOfferPriceByVin(state, vin),
    tradeAllowance: getDealerOfferTradeInAllowanceByVin(state, vin),
    tradeBalance: getDealerOfferTradeInBalanceByVin(state, vin),
    tradeCashOut: getDealerOfferTradeInCashOutByVin(state, vin),
    fees: getDealerOfferFeesByVin(state, vin),
    taxes: getDealerOfferTaxesByVin(state, vin),
    rebates: getDealerOfferRebatesByVin(state, vin),
    accessories: getDealerOfferAccessoriesByVin(state, vin),
    warranties: getDealerOfferWarrantiesByVin(state, vin),
    paymentType: getDealerOfferTypeByVin(state, vin) as string,
    lender: getDealerOfferLenderByVin(state, vin),
    downPayment: getDealerOfferDownPaymentByVin(state, vin),
    rate: getDealerOfferInterestRateByVin(state, vin),
    term: getDealerOfferTermByVin(state, vin),
    amountFinanced: getDealerOfferAmountFinancedByVin(state, vin),
    distance: getDealerOfferDistanceByVin(state, vin),
    distanceLabel: state.account.localization.distance?.label,
});

export default connect(mapStateToProps)(ReviewPage);

export const DetailsContainer = styled("div")`
    display: flex;
    flex-flow: column;
    padding: 0 0 2em 0;
`;

export const DetailsBlock = styled("div")`
    display: flex;
    flex-flow: column;
    padding: 0.5em 0 0.5em;
`;

export const DetailsHeader = styled("div")`
    font-size: 20px;
    border-bottom: 2px solid;
    margin: 5px 0 10px;
`;

export const DetailsRow = styled("div")`
    display: flex;
    flex-flow: row;
    width: 100%;
    justify-content: space-between;
    border-bottom: 1px dashed #ccc;
    margin: 3px 0;
`;

export const Label = styled("span")`
    white-space: nowrap;
`;

export const Value = styled("span")`
    font-weight: 200;
    text-align: end;
`;
